/**
 * SharedRoomPage — public view of a shared chat room.
 * Accessible at `/share/room/:token` without authentication.
 *
 * Mirrors the authenticated ChatPage layout:
 *   - Left sidebar with persona profile + "Ask about" chips (open by default)
 *   - Right column with the message thread
 *   - Disabled composer with a "Log in" CTA
 *   - No Share button, no Survey Answers panel
 *
 * Mandatory email-OTP gate runs as step 2 (after access-mode validation,
 * before any room data is fetched) for everyone — guests and platform-
 * authenticated users alike. Mirrors the `ShareTokenPage` phase machine.
 */

import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useOutletContext, useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AlertCircle, ChevronDown, ChevronUp, Loader2, Lock } from '@digital-twins/design-system/icons'
import {
  computeShareTitle,
  type SharedShellOutletContext,
} from '@/spa/layouts/SharedAppShellLayout'

import { setReferralCookies, getReferralCookies } from '@/lib/utils/referral-cookie'
import {
  ensureShareGuestIntent,
  hasValidGuestSessionToken,
} from '@/lib/utils/share-guest-intent'
import { track, AnalyticsEvents } from '@/lib/services/analytics'
import { Text } from '@/components/ui/Text'
import { Heading } from '@/components/ui/Heading'
import { Card, CardContent } from '@/components/ui/card'
import { PageLayout } from '@/components/layout/PageLayout'
import { PersonaThumbImg } from '@/components/shared/PersonaThumbImg'
import { ChatComposer as ChatComposerPrimitive } from '@/components/chat/ChatComposer'
import { blockedFeatureMessage } from '@/lib/blocked-feature-error'
import { publicRoomsApi } from '@/lib/api'
import { ChatSidebar } from '@/pages/ChatPage/ChatSidebar/ChatSidebar'
import { ConversationSidebarPanel } from '@/components/ConversationSidebarPanel/ConversationSidebarPanel'
import { useIsWideViewport } from '@/hooks/useIsWideViewport'
import { ChatThread } from '@/pages/ChatPage/ChatThread/ChatThread'
import { orderRoomMessages } from '@/pages/ChatPage/orderRoomMessages'
import { EmailGateForm } from '@/features/studies/sharing/page/components/EmailGateForm'
import { useTheme } from '@/lib/theme-provider'
import { resolveLegacyFairgenLogoUrl } from '@/lib/theme/fairgen-default-brand'

import { ChatRoomMessageDto, ChatRoomParticipantDto, ChatRoomReactionsEvent } from '@digital-twins/shared-types'

type AccessPhase = 'checking' | 'email_gate' | 'loading' | 'ready' | 'error'

type PublicRoomData = Awaited<ReturnType<typeof publicRoomsApi.getRoom>>

/** An ask-about turn this page started and is waiting on. */
interface PendingAskAbout {
  turnId: string
  participantId: string
  participantName: string
  chipText: string
  startedAt: number
}

type AskAboutNotice = 'busy' | 'slow'

/** Why the server refused ask-about for the rest of this visit. */
type AskAboutRejection = 'notActive' | 'capReached'

const MAX_AUDIENCE_CHARS = 28

/** How often the public room read is refetched while an ask-about reply is pending. */
export const ASK_ABOUT_POLL_INTERVAL_MS = 2_000
/**
 * Polling stops after this long; the guest sees the "taking longer" line.
 * Applies both to the guest's own pending reply and to waiting out someone
 * else's turn (`turnInProgress`).
 */
export const ASK_ABOUT_POLL_TIMEOUT_MS = 120_000

const ASK_ABOUT_NOTICE_COPY: Record<AskAboutNotice, string> = {
  busy: 'Twins are answering. Try again in a moment.',
  slow: 'This is taking longer than usual.',
}

const ROOM_ENDED_COPY = 'This conversation has ended.'
// Backend code on the 400 for a send into a room that is not active.
const ROOM_NOT_ACTIVE_CODE = 'room_not_active'

/** The reply to a turn: the twin's answer, or the system line saying it could not answer. */
function findTurnReply(
  messages: Array<ChatRoomMessageDto>,
  turnId: string,
): ChatRoomMessageDto | null {
  return (
    messages.find((m) => {
      const metadata = m.metadata as Record<string, unknown> | null
      if (!metadata || metadata.responseTurnId !== turnId) {
        return false
      }
      if (m.senderType === 'twin') {
        return true
      }
      return m.senderType === 'system' && metadata.event === 'participant_response_failed'
    }) ?? null
  )
}

function readCapReached(err: unknown): boolean {
  return Boolean(err && typeof err === 'object' && (err as { capReached?: unknown }).capReached === true)
}

function readErrorCode(err: unknown): string | null {
  if (err && typeof err === 'object' && typeof (err as { code?: unknown }).code === 'string') {
    return (err as { code: string }).code
  }
  return null
}

function readErrorStatus(err: unknown): number | null {
  if (err && typeof err === 'object' && typeof (err as { status?: unknown }).status === 'number') {
    return (err as { status: number }).status
  }
  return null
}

function buildHeaderLabel(
  participants: Array<ChatRoomParticipantDto>,
  populationName: string | null | undefined,
): string {
  if (participants.length === 1) {
    return `Interview with ${participants[0].displayName}`
  }
  const countPart = `+${participants.length}`
  if (populationName) {
    const truncated =
      populationName.length > MAX_AUDIENCE_CHARS
        ? `${populationName.slice(0, MAX_AUDIENCE_CHARS).trimEnd()}…`
        : populationName
    return `Focus Group with ${countPart} from ${truncated}`
  }
  return `Focus Group with ${countPart}`
}

export default function SharedRoomPage() {
  const { token } = useParams<{ token: string }>()
  const [searchParams] = useSearchParams()
  const { resolvedTheme } = useTheme()
  const { t } = useTranslation(['app', 'common'])
  const outletContext = useOutletContext<SharedShellOutletContext | undefined>()
  const setStudyName = outletContext?.setStudyName
  const branding = outletContext?.branding ?? null
  const whiteLabel = outletContext?.whiteLabel === true
  const [phase, setPhase] = useState<AccessPhase>('checking')
  const [data, setData] = useState<PublicRoomData | null>(null)
  const [reactionsSnapshot, setReactionsSnapshot] = useState<ChatRoomReactionsEvent | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [brandLogo, setBrandLogo] = useState<{ url: string | null; urlOnDark: string | null }>({
    url: null,
    urlOnDark: null,
  })
  // Guest questions this page asked in this session. Shown at once from the
  // ask-about response, then superseded by the persisted copy in the room read.
  const [askedQuestions, setAskedQuestions] = useState<Array<ChatRoomMessageDto>>([])
  const [isSubmittingAskAbout, setIsSubmittingAskAbout] = useState(false)
  const [pendingAskAbout, setPendingAskAbout] = useState<PendingAskAbout | null>(null)
  const [askAboutNotice, setAskAboutNotice] = useState<AskAboutNotice | null>(null)
  const [askAboutRejection, setAskAboutRejection] = useState<AskAboutRejection | null>(null)
  // Set while the room reports someone else's turn in progress (or after a 409);
  // the value is when the wait started, for the poll cap.
  const [otherTurnSince, setOtherTurnSince] = useState<number | null>(null)
  const isWideViewport = useIsWideViewport()
  const [sidebarOpen, setSidebarOpen] = useState(isWideViewport)
  const sidebarTriggerRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    const unavailable = phase === 'error'
    outletContext?.setShareUnavailable?.(unavailable)
  }, [phase, outletContext])

  useEffect(() => {
    const refCode = searchParams.get('ref')
    if (refCode && !getReferralCookies().referralCode) {
      // Only write if no cookie exists yet — preserves 'referral_link' source
      // set by SpaRootLayoutEffects when the visitor arrived via a plain referral link.
      setReferralCookies(refCode, 'chat_share')
    }
    track(AnalyticsEvents.CHAT_SHARE_VIEWED, { token })
  }, [token, searchParams])

  // Step 1: validate share token via access-mode (no auth required).
  // Step 2: mandatory email gate unless a valid guest_session JWT scoped to
  // this exact room share token already exists in sessionStorage.
  useEffect(() => {
    if (!token) return
    let cancelled = false

    async function checkAccess() {
      try {
        const result = await publicRoomsApi.getAccessMode(token!)
        if (cancelled) return

        setBrandLogo({
          url: resolveLegacyFairgenLogoUrl(result.brandLogoUrl),
          urlOnDark: resolveLegacyFairgenLogoUrl(result.brandLogoUrlOnDark),
        })

        if (result.mode === 'unavailable') {
          setErrorMessage(t('sharing.invalidLink.body'))
          setPhase('error')
          return
        }

        ensureShareGuestIntent(`/share/room/${encodeURIComponent(token!)}`)

        if (hasValidGuestSessionToken(token!, 'room')) {
          setPhase('loading')
        } else {
          setPhase('email_gate')
        }
      } catch {
        if (!cancelled) {
          setErrorMessage('Failed to check room access')
          setPhase('error')
        }
      }
    }

    checkAccess()
    return () => {
      cancelled = true
    }
  }, [token, t])

  // Step 3: fetch the room with the guest JWT once the gate is cleared.
  const loadRoom = useCallback(async () => {
    if (!token) return
    try {
      const result = await publicRoomsApi.getRoom(token)
      setData(result)
      setOtherTurnSince(result.turnInProgress ? Date.now() : null)
      setPhase('ready')
    } catch (err: unknown) {
      const blocked = blockedFeatureMessage(err)
      const message = err instanceof Error ? err.message : ''
      if (message.includes('Email verification required')) {
        setPhase('email_gate')
        return
      }
      setErrorMessage(blocked ?? (message || 'Failed to load shared room'))
      setPhase('error')
    }
  }, [token])

  useEffect(() => {
    if (phase === 'loading') {
      loadRoom()
    }
  }, [phase, loadRoom])

  // Step 11: reactions on load, and again after every room read (the poll),
  // applied only when the snapshot's revision is newer. Chips are additive:
  // a failed read keeps what is shown.
  useEffect(() => {
    if (!token || !data) return
    let cancelled = false
    Promise.resolve()
      .then(() => publicRoomsApi.getReactions(token))
      .then((snapshot) => {
        if (cancelled || !Array.isArray(snapshot?.reactions)) return
        setReactionsSnapshot((prev) =>
          snapshot.revision > (prev?.revision ?? -1) ? snapshot : prev,
        )
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [token, data])

  useEffect(() => {
    if (!setStudyName) return
    if (!data) {
      setStudyName('')
      return
    }
    const label = data.room.title ?? buildHeaderLabel(data.room.participants, data.room.populationName)
    setStudyName(label)
  }, [setStudyName, data])

  const handleEmailVerified = useCallback(() => {
    setPhase('loading')
  }, [])

  // Everything renders from the public room read; a question asked here shows
  // from the ask-about response only until that read carries its persisted copy.
  // Same display order as the owner view: the assistant's chart closes its round.
  const allMessages = useMemo(() => {
    if (!data) return []
    const persistedIds = new Set(data.messages.map((m) => m.id))
    const notYetPersisted = askedQuestions.filter((q) => !persistedIds.has(q.id))
    return orderRoomMessages([...data.messages, ...notYetPersisted])
  }, [data, askedQuestions])

  // The divider sits before the first question this guest asked in this
  // session, matched by message id. The shared read cannot tell a guest
  // question from an owner one (both are persisted user messages), and an
  // original-count index would drift if the room grows during polling.
  const guestDividerIndex = useMemo(() => {
    if (askedQuestions.length === 0) return undefined
    const askedIds = new Set(askedQuestions.map((q) => q.id))
    const index = allMessages.findIndex((m) => askedIds.has(m.id))
    return index >= 0 ? index : undefined
  }, [allMessages, askedQuestions])

  const isRoomActive = data?.room.status === 'active'
  const isAskAboutLocked =
    isSubmittingAskAbout ||
    pendingAskAbout !== null ||
    otherTurnSince !== null ||
    !isRoomActive ||
    askAboutRejection !== null

  // One poll of the public room read, for two waits: the reply to this
  // guest's pending turn, or the end of someone else's turn (no queue: chips
  // stay disabled until the room reports no turn in progress).
  useEffect(() => {
    if (!token) return
    const pending = pendingAskAbout
    const startedAt = pending?.startedAt ?? otherTurnSince
    if (startedAt === null) return
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined

    async function poll() {
      try {
        const result = await publicRoomsApi.getRoom(token!)
        if (cancelled) return
        setData(result)
        if (pending) {
          const reply = findTurnReply(result.messages, pending.turnId)
          if (reply) {
            if (reply.senderType === 'twin') {
              track(AnalyticsEvents.GUEST_ASK_ABOUT_REPLY_RECEIVED, {
                token,
                participantId: pending.participantId,
                participantName: pending.participantName,
                chipText: pending.chipText,
                responseLength: reply.content.length,
              })
            }
            setPendingAskAbout(null)
            setOtherTurnSince(result.turnInProgress ? Date.now() : null)
            return
          }
        } else if (!result.turnInProgress) {
          setOtherTurnSince(null)
          setAskAboutNotice((prev) => (prev === 'busy' ? null : prev))
          return
        }
      } catch {
        // A failed refetch is retried on the next tick.
        if (cancelled) return
      }
      if (Date.now() - startedAt! >= ASK_ABOUT_POLL_TIMEOUT_MS) {
        setPendingAskAbout(null)
        setOtherTurnSince(null)
        setAskAboutNotice('slow')
        return
      }
      timer = setTimeout(poll, ASK_ABOUT_POLL_INTERVAL_MS)
    }

    timer = setTimeout(poll, ASK_ABOUT_POLL_INTERVAL_MS)
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [pendingAskAbout, otherTurnSince, token])

  const handleAskAbout = useCallback(
    async (text: string) => {
      if (!data || !token || isAskAboutLocked) return

      const participant = data.room.participants.find(
        (p) => p.conversationStarters?.includes(text),
      ) ?? data.room.participants[0]
      if (!participant) return

      setIsSubmittingAskAbout(true)
      setAskAboutNotice(null)

      track(AnalyticsEvents.GUEST_ASK_ABOUT_CLICKED, {
        token,
        participantId: participant.id,
        participantName: participant.displayName,
        chipText: text,
      })

      try {
        const result = await publicRoomsApi.askAbout(
          token,
          participant.id,
          text,
        )
        setAskedQuestions((prev) => [...prev, result.message])
        setPendingAskAbout({
          turnId: result.turnId,
          participantId: participant.id,
          participantName: participant.displayName,
          chipText: text,
          startedAt: Date.now(),
        })
      } catch (err: unknown) {
        const status = readErrorStatus(err)
        if (readCapReached(err)) {
          setAskAboutRejection('capReached')
        } else if (status === 409) {
          setAskAboutNotice('busy')
          setOtherTurnSince(Date.now())
        } else if (status === 400 && readErrorCode(err) === ROOM_NOT_ACTIVE_CODE) {
          setAskAboutRejection('notActive')
        }
        // Other failures (other 400s included) stay silent and leave the chips
        // enabled: guests get no error toasts here.
      } finally {
        setIsSubmittingAskAbout(false)
      }
    },
    [token, data, isAskAboutLocked],
  )

  if (phase === 'checking') {
    return (
      <PageLayout variant="fullBleed" className="bg-twin-chat-bg">
        <div
          data-testid="shared-room-checking"
          className="flex h-full min-h-[60vh] items-center justify-center"
        >
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" />
            <Text size="sm" color="muted">Checking access...</Text>
          </div>
        </div>
      </PageLayout>
    )
  }

  if (phase === 'email_gate' && token) {
    return (
      <PageLayout variant="fullBleed" className="bg-twin-chat-bg">
        <div
          data-testid="shared-room-email-gate"
          className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-md bg-background/60"
        >
          <EmailGateForm
            shareToken={token}
            kind="room"
            onVerified={handleEmailVerified}
          />
        </div>
      </PageLayout>
    )
  }

  if (phase === 'loading') {
    return (
      <PageLayout variant="fullBleed" className="bg-twin-chat-bg">
        <div
          data-testid="shared-room-loading"
          className="flex h-full min-h-[60vh] items-center justify-center"
        >
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" />
            <Text size="sm" color="muted">Loading conversation...</Text>
          </div>
        </div>
      </PageLayout>
    )
  }

  if (phase === 'error' || !data) {
    const linkUnavailable =
      errorMessage?.includes('not available') ||
      errorMessage?.includes('expired') ||
      errorMessage === t('sharing.invalidLink.body')
    return (
      <PageLayout variant="fullBleed" className="bg-twin-chat-bg">
        <div
          data-testid="shared-room-error"
          className="flex h-full min-h-[60vh] items-center justify-center"
        >
          <Card className="w-full max-w-md">
            <CardContent className="flex flex-col items-center gap-4 py-12 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10">
                {linkUnavailable ? (
                  <Lock className="h-7 w-7 text-destructive" />
                ) : (
                  <AlertCircle className="h-7 w-7 text-destructive" />
                )}
              </div>
              <Heading level={2}>
                {linkUnavailable
                  ? t('sharing.invalidLink.title')
                  : t('studyReport.shareUnavailable.title')}
              </Heading>
              <Text size="sm" color="muted" className="max-w-xs">
                {linkUnavailable
                  ? t('sharing.invalidLink.body')
                  : errorMessage ?? t('studyReport.shareUnavailable.body')}
              </Text>
            </CardContent>
          </Card>
        </div>
      </PageLayout>
    )
  }

  // Reference brandLogo so the variable isn't tree-shaken or flagged unused;
  // it's set during access-mode for future header chrome variants.
  void brandLogo

  const { room, ownerOrg } = data
  const participants = room.participants
  const headerLabel = buildHeaderLabel(participants, room.populationName)
  const sharedDisplayName = room.title ?? headerLabel
  const ownerOrgName = branding?.orgName ?? ownerOrg?.name ?? ''
  const helmetTitle = computeShareTitle(whiteLabel, sharedDisplayName, ownerOrgName)
  const ChevronIcon = sidebarOpen ? ChevronUp : ChevronDown
  const orgLogoUrl = ownerOrg
    ? (resolvedTheme === 'dark' && ownerOrg.logoUrlOnDark ? ownerOrg.logoUrlOnDark : ownerOrg.logoUrl)
    : null

  const isRoomEnded = room.status === 'closed' || askAboutRejection === 'notActive'
  let askAboutStatusLine: ReactNode = null
  if (askAboutRejection === 'capReached') {
    askAboutStatusLine = (
      <div className="mb-2 flex flex-col items-center gap-1" data-testid="shared-room-ask-about-cap">
        <Text size="sm" className="text-center font-medium text-twin-chat-fg">
          {t('chatChrome.messageLimitReached')}
        </Text>
        <Text size="xs" className="text-center text-twin-chat-fg-muted">
          {t('chatChrome.signUpToContinue')}
        </Text>
      </div>
    )
  } else if (isRoomEnded) {
    askAboutStatusLine = (
      <Text
        size="xs"
        className="mb-2 text-center text-twin-chat-fg-muted"
        role="status"
        data-testid="shared-room-ask-about-ended"
      >
        {ROOM_ENDED_COPY}
      </Text>
    )
  }

  const guestComposer = (
    <div className="shrink-0 px-4 pb-4">
      {askAboutStatusLine}
      {!askAboutStatusLine && askAboutNotice && (
        <Text
          size="xs"
          className="mb-2 text-center text-twin-chat-fg-muted"
          role="status"
          aria-live="polite"
          data-testid="shared-room-ask-about-notice"
        >
          {ASK_ABOUT_NOTICE_COPY[askAboutNotice]}
        </Text>
      )}
      <Text
        size="xs"
        className="mb-2 text-center text-twin-chat-fg-muted"
        data-testid="shared-room-fairgen-attribution"
      >
        This conversation was created using Fairgen
      </Text>
      <ChatComposerPrimitive
        onSend={() => {}}
        disabled
        disabledMessage="Sign up to chat with this twin"
        disabledCtaHref="/register"
        disabledCtaLabel="Create free account"
      />
    </div>
  )

  return (
    <PageLayout variant="fullBleed" className="bg-twin-chat-bg">
      {helmetTitle && (
        <Helmet>
          <title>{helmetTitle}</title>
        </Helmet>
      )}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">

        {/* ── Header — mirrors ChatPage but no Share or Survey Answers buttons ── */}
        <div className="shrink-0 border-b border-twin-chat-divider/20">
          <div className="grid grid-cols-3 items-center px-8 pb-3 pt-8">
            <div className="flex items-center gap-2">
              <button
                type="button"
                data-testid="chat-sidebar-toggle"
                ref={sidebarTriggerRef}
                aria-expanded={sidebarOpen}
                aria-label={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
                onClick={() => setSidebarOpen((o) => !o)}
                className="flex items-center gap-1.5 rounded-md px-1 py-0.5 transition-colors hover:bg-twin-chat-bubble/20"
              >
                <Text
                  size="sm"
                  className="font-medium text-twin-chat-fg"
                  data-testid="chat-page-header-title"
                >
                  {headerLabel}
                </Text>
                <ChevronIcon className="h-3.5 w-3.5 shrink-0 text-twin-chat-muted" />
              </button>
            </div>

            <div className="flex items-center justify-center gap-2">
              {participants.length === 1 && participants[0].imageUrl && (
                <PersonaThumbImg
                  src={participants[0].imageUrl}
                  size={64}
                  alt={participants[0].displayName}
                  className="h-8 w-8 shrink-0 rounded-full object-cover"
                />
              )}
              <Text size="sm" className="font-medium text-twin-chat-fg truncate">
                {room.title ?? headerLabel}
              </Text>
            </div>

            <div className="flex items-center justify-end">
              {orgLogoUrl && (
                <img
                  src={orgLogoUrl}
                  alt={ownerOrg?.name ?? 'Organization'}
                  data-testid="shared-room-org-logo"
                  className="h-7 max-w-[120px] object-contain"
                />
              )}
            </div>
          </div>
        </div>

        {/* ── Two-column area: sidebar + chat ── */}
        <div className="flex min-h-0 flex-1 overflow-hidden">

          {/* ── Sidebar panel — open by default ── */}
          <ConversationSidebarPanel
            open={sidebarOpen}
            onOpenChange={setSidebarOpen}
            triggerRef={sidebarTriggerRef}
          >
            <ChatSidebar
              room={room}
              participants={participants}
              participantStates={{}}
              roomStatus={room.status}
              roomTitle={room.title}
              canUpdateSettings={false}
              onSend={
                branding?.chatRoomsEnabled === false
                  ? undefined
                  : (text) => handleAskAbout(text)
              }
              startersDisabled={isAskAboutLocked}
              className="flex-1"
            />
          </ConversationSidebarPanel>

          {/* ── Chat column ── */}
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="flex min-h-0 w-full flex-1 flex-col">
              <ChatThread
                columnClassName="max-w-3xl"
                messages={allMessages}
                participants={participants}
                roomStatus={room.status}
                onSend={() => {}}
                thinkingParticipantIds={pendingAskAbout ? [pendingAskAbout.participantId] : []}
                guestDividerIndex={guestDividerIndex}
                composerOverride={guestComposer}
                emptyStateText="No messages in this conversation yet."
                reactions={reactionsSnapshot?.reactions}
              />
            </div>
          </div>

        </div>
      </div>
    </PageLayout>
  )
}
